// @owners { team: patients-team }
import { SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  ActionSheetV2,
  Button,
  Column,
  InputRadio,
  InputRadioGroup,
  InputText,
  LgPadding,
  Toast,
  ToastContext,
} from '@alto/design-system';
import { InputLabelRow } from '@alto/design-system/src/components/inputs/src/InputText/InputLabelRow';
import { omit } from 'lodash';
import React, { useCallback, useContext } from 'react';
import { useController, useForm } from 'react-hook-form';
// eslint-disable-next-line import/no-deprecated
import { clearUserError, updateUser } from '~shared/actions/users';
import { DEFAULT_ERROR_ALERT_MESSAGE, DEFAULT_ERROR_ALERT_TITLE, GENDERS, PRONOUNS } from '~shared/constants';
import getSelectedUser from '~shared/features/users/selectors/getSelectedUser';
import { mapControllerToInputProps } from '~shared/helpers/mapFieldToInputProps';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type Gender } from '~shared/types';

type Values = {
  date_of_birth: string;
  first_name: string;
  gender: Gender | undefined;
  last_name: string;
  preferred_first_name: string;
  preferred_pronoun: string;
};

const getRadioOptions = (collection: typeof GENDERS | typeof PRONOUNS) => {
  return Object.entries(collection).map(([key, value]) => {
    return (
      <InputRadio
        label={value}
        value={key}
        key={key}
        radioPlacement="left"
      />
    );
  });
};

const CHANGE_NAME_OR_DOB_COPY =
  'To ensure your privacy, please contact us if you wish to change your name or date of birth';
const CHANGE_PREFERRED_NAME_COPY =
  'If you go by a name other than your legal name, please share it with us so we can address you correctly';

export const PersonalInfoActionSheet = () => {
  const { closeActionSheet } = useContext(ActionSheetContext);
  const { addToast } = useContext(ToastContext);
  const dispatch = useDispatchShared();
  const user = useSelectorShared(getSelectedUser);
  const loading = useSelectorShared((state) => state.ui.loading.updateUserLoading);

  const handleClose = useCallback(() => {
    dispatch(clearUserError());
  }, [dispatch]);

  const updateUserPersonalInfo = useCallback(
    async (data: Values) => {
      if (!user?.id) {
        addToast(<Toast variant="error">{`${DEFAULT_ERROR_ALERT_TITLE} ${DEFAULT_ERROR_ALERT_MESSAGE}`}</Toast>);
        return;
      }

      const params = omit(data, ['first_name', 'last_name', 'date_of_birth']);
      const { success } = await dispatch(
        // eslint-disable-next-line import/no-deprecated
        updateUser({
          id: user.id,
          ...params,
        }),
      );

      if (success) {
        addToast(<Toast>Saved personal information!</Toast>);
        closeActionSheet();
      }
    },
    [addToast, closeActionSheet, dispatch, user?.id],
  );

  const handleSavePersonalInfo = useCallback(
    async (data: Values) => {
      await updateUserPersonalInfo(data);
    },
    [updateUserPersonalInfo],
  );

  const defaultValues = {
    date_of_birth: user?.date_of_birth ?? '',
    first_name: user?.first_name ?? '',
    last_name: user?.last_name ?? '',
    gender: user?.gender ?? undefined,
    preferred_first_name: user?.preferred_first_name ?? '',
    preferred_pronoun: user?.preferred_pronoun ?? '',
  };

  const { control, handleSubmit, setValue, getValues } = useForm({ defaultValues });

  const firstNameController = useController({
    name: 'first_name',
    control,
  });
  const lastNameController = useController({
    name: 'last_name',
    control,
  });
  const preferredFirstNameController = useController({
    name: 'preferred_first_name',
    control,
  });
  const dateOfBirthController = useController({
    name: 'date_of_birth',
    control,
  });
  const genderController = useController({
    name: 'gender',
    control,
    rules: {
      required: 'Sex cannot be empty',
    },
  });

  return (
    <ActionSheetV2
      handleClose={handleClose}
      title="Edit personal info"
      description={CHANGE_NAME_OR_DOB_COPY}
      analyticsName="edit_personal_info"
      buttons={[
        <Button
          key="save-personal-info-button"
          label="Save personal info"
          loadingLabel="Saving personal info"
          loading={loading}
          onPress={handleSubmit(handleSavePersonalInfo)}
        />,
        <Button
          key="dismiss-personal-info-button"
          label="Cancel"
          type="tertiary"
          onPress={() => {
            handleClose();
            closeActionSheet();
          }}
        />,
      ]}
    >
      <LgPadding topPadding={SPACING.STATIC.NONE}>
        <Column gap="MD">
          <InputText
            label="First name"
            textContentType="name"
            placeholder="First"
            disabled
            {...mapControllerToInputProps(firstNameController)}
          />
          <InputText
            label="Last name"
            textContentType="familyName"
            placeholder="Last"
            disabled
            {...mapControllerToInputProps(lastNameController)}
          />
          <InputText
            label="Preferred name"
            textContentType="name"
            placeholder="Preferred name"
            caption={CHANGE_PREFERRED_NAME_COPY}
            {...mapControllerToInputProps(preferredFirstNameController)}
          />
          <InputText
            label="Date of birth"
            textContentType="birthdate"
            placeholder="MM/DD/YYYY"
            disabled
            {...mapControllerToInputProps(dateOfBirthController)}
          />
          <Column>
            <InputLabelRow
              label="Choose sex"
              error={genderController.fieldState.error?.message}
              sublabel="This should match your insurance."
              required
            />
            <InputRadioGroup
              label="Choose sex"
              onValueChange={(value) => {
                setValue('gender', value as Gender);
              }}
              defaultValue={getValues('gender')}
            >
              {getRadioOptions(GENDERS)}
            </InputRadioGroup>
          </Column>
          <Column>
            <InputLabelRow label="Choose pronoun" />
            <InputRadioGroup
              label="Choose pronoun"
              onValueChange={(value) => {
                setValue('preferred_pronoun', value);
              }}
              defaultValue={getValues('preferred_pronoun')}
            >
              {getRadioOptions(PRONOUNS)}
            </InputRadioGroup>
          </Column>
        </Column>
      </LgPadding>
    </ActionSheetV2>
  );
};
